import React, { useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  message,
} from 'antd'
import {
  DownloadOutlined,
  ReloadOutlined,
  RiseOutlined,
  TrophyOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import type { ColumnsType } from 'antd/es/table'
import { api } from '@services/api'

const { RangePicker } = DatePicker
const { Option } = Select

interface PerformanceSummary {
  period?: string
  total_trades: number
  total_profit: number
  win_rate: number
  profit_factor: number
  active_days: number
  avg_daily_profit: number
  best_day: number
  worst_day: number
  max_drawdown: number
}

interface TokenRankingItem {
  rank: number
  token_id: number
  token_name: string
  total_trades: number
  total_pnl: number
  total_volume: number
}

const toNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : fallback
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : fallback
  }
  return fallback
}

const toPercent = (value: unknown) => `${toNumber(value).toFixed(2)}%`

const exportCsv = (filename: string, rows: Array<Record<string, unknown>>) => {
  if (!rows.length) {
    message.warning('暂无可导出的数据')
    return
  }
  const headers = Object.keys(rows[0])
  const escapeCsv = (cell: unknown) => {
    const text = String(cell ?? '')
    if (text.includes(',') || text.includes('"') || text.includes('\n')) {
      return `"${text.replace(/"/g, '""')}"`
    }
    return text
  }
  const content = [
    headers.join(','),
    ...rows.map((row) => headers.map((h) => escapeCsv(row[h])).join(',')),
  ].join('\n')
  const blob = new Blob([`\uFEFF${content}`], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

const Analytics: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<number>(30)
  const [selectedDateRange, setSelectedDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null)

  const { data: analytics, isLoading, refetch, isError } = useQuery<PerformanceSummary>({
    queryKey: ['analytics', 'performance', selectedPeriod],
    queryFn: () => api.statistics.getPerformanceSummary(selectedPeriod),
    refetchInterval: 60000, // 1分钟刷新
  })

  const { data: tokenRankingResp, isLoading: tokenLoading } = useQuery<any>({
    queryKey: ['analytics', 'token-ranking', selectedPeriod],
    queryFn: () => api.statistics.getTokenRanking(selectedPeriod),
  })

  const refreshMutation = useMutation({
    mutationFn: () => api.statistics.refreshStatistics?.(Math.min(selectedPeriod, 30)),
    onSuccess: async () => {
      message.success('统计数据刷新完成')
      await refetch()
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || error?.message || '刷新统计失败')
    },
  })

  const rankingData: TokenRankingItem[] = useMemo(() => {
    const list = tokenRankingResp?.data?.ranking || tokenRankingResp?.ranking || []
    if (!Array.isArray(list)) return []
    return list.map((item: any, index: number) => ({
      rank: toNumber(item?.rank, index + 1),
      token_id: toNumber(item?.token_id),
      token_name: item?.token_name || `Token-${toNumber(item?.token_id)}`,
      total_trades: toNumber(item?.total_trades),
      total_pnl: toNumber(item?.total_pnl),
      total_volume: toNumber(item?.total_volume),
    }))
  }, [tokenRankingResp])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value)
  }

  const onDateRangeChange = (dates: [dayjs.Dayjs, dayjs.Dayjs] | null) => {
    setSelectedDateRange(dates)
    if (!dates) return
    const days = Math.max(1, dates[1].diff(dates[0], 'day') + 1)
    setSelectedPeriod(days)
  }

  const tokenColumns: ColumnsType<TokenRankingItem> = [
    {
      title: '排名',
      dataIndex: 'rank',
      key: 'rank',
      width: 80,
      render: (rank) => (
        <div style={{ textAlign: 'center' }}>
          {rank === 1 && <TrophyOutlined style={{ color: '#faad14', marginRight: 4 }} />}
          {rank}
        </div>
      ),
    },
    {
      title: 'Token名称',
      dataIndex: 'token_name',
      key: 'token_name',
      ellipsis: true,
    },
    {
      title: '交易次数',
      dataIndex: 'total_trades',
      key: 'total_trades',
      align: 'right',
    },
    {
      title: '总盈亏',
      dataIndex: 'total_pnl',
      key: 'total_pnl',
      align: 'right',
      render: (pnl) => (
        <span style={{ color: toNumber(pnl) >= 0 ? '#3f8600' : '#cf1322', fontWeight: 'bold' }}>
          {formatCurrency(toNumber(pnl))}
        </span>
      ),
    },
    {
      title: '交易量',
      dataIndex: 'total_volume',
      key: 'total_volume',
      align: 'right',
      render: (volume) => formatCurrency(toNumber(volume)),
    },
    {
      title: '交易效率',
      key: 'efficiency',
      align: 'right',
      render: (_, row) => {
        if (!row.total_trades) return '-'
        return formatCurrency(row.total_pnl / row.total_trades)
      },
    },
  ]

  const exportPerformance = () => {
    if (!analytics) {
      message.warning('暂无可导出的统计数据')
      return
    }
    exportCsv(`performance_${selectedPeriod}d.csv`, [
      {
        period: analytics.period || `${selectedPeriod}d`,
        total_trades: analytics.total_trades,
        total_profit: analytics.total_profit,
        win_rate_percent: analytics.win_rate,
        profit_factor: analytics.profit_factor,
        max_drawdown_percent: analytics.max_drawdown,
        active_days: analytics.active_days,
        avg_daily_profit: analytics.avg_daily_profit,
        best_day: analytics.best_day,
        worst_day: analytics.worst_day,
      },
    ])
  }

  return (
    <div className="page-inner">
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            value={selectedPeriod}
            onChange={setSelectedPeriod}
            style={{ width: 120 }}
          >
            <Option value={7}>最近7天</Option>
            <Option value={30}>最近30天</Option>
            <Option value={90}>最近90天</Option>
            <Option value={365}>最近1年</Option>
          </Select>

          <RangePicker
            value={selectedDateRange}
            onChange={(dates) => onDateRangeChange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
            placeholder={['开始日期', '结束日期']}
          />

          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
            刷新数据
          </Button>
          <Button loading={refreshMutation.isPending} onClick={() => refreshMutation.mutate()}>
            重算统计
          </Button>
          <Button icon={<DownloadOutlined />} onClick={exportPerformance}>
            导出总览
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={() =>
              exportCsv(
                `token_ranking_${selectedPeriod}d.csv`,
                rankingData.map((item) => ({ ...item }))
              )
            }
          >
            导出Token榜单
          </Button>
        </Space>
      </Card>

      {isError && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="统计数据加载异常"
          description="请先点击“重算统计”生成最近数据，或稍后再试。"
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12} lg={8}>
          <Card size="small">
            <Statistic title="统计区间" value={analytics?.period || `${selectedPeriod} 天`} />
          </Card>
        </Col>
        <Col xs={24} md={12} lg={8}>
          <Card size="small">
            <Statistic title="总交易次数" value={toNumber(analytics?.total_trades)} prefix={<RiseOutlined />} />
          </Card>
        </Col>
        <Col xs={24} md={12} lg={8}>
          <Card size="small">
            <Statistic
              title="总收益"
              value={toNumber(analytics?.total_profit)}
              precision={2}
              suffix="USDT"
              valueStyle={{ color: toNumber(analytics?.total_profit) >= 0 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} md={12} lg={8}>
          <Card size="small">
            <Statistic title="胜率" value={toPercent(analytics?.win_rate)} />
          </Card>
        </Col>
        <Col xs={24} md={12} lg={8}>
          <Card size="small">
            <Statistic title="盈亏因子" value={toNumber(analytics?.profit_factor).toFixed(2)} />
          </Card>
        </Col>
        <Col xs={24} md={12} lg={8}>
          <Card size="small">
            <Statistic title="最大回撤" value={toPercent(analytics?.max_drawdown)} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
        <Col xs={24} md={12} lg={8}>
          <Card size="small">
            <Statistic title="活跃交易日" value={toNumber(analytics?.active_days)} suffix="天" />
          </Card>
        </Col>
        <Col xs={24} md={12} lg={8}>
          <Card size="small">
            <Statistic title="日均收益" value={toNumber(analytics?.avg_daily_profit)} precision={2} suffix="USDT" />
          </Card>
        </Col>
        <Col xs={24} md={12} lg={8}>
          <Card size="small">
            <Statistic
              title="最佳/最差单日"
              value={`${formatCurrency(toNumber(analytics?.best_day))} / ${formatCurrency(toNumber(analytics?.worst_day))}`}
            />
          </Card>
        </Col>
      </Row>

      <Card title="Token表现排名" size="small" style={{ marginTop: 16 }}>
        {rankingData.length === 0 && !tokenLoading ? (
          <Empty description="暂无Token交易统计数据" />
        ) : (
          <Table
            columns={tokenColumns}
            dataSource={rankingData}
            rowKey="token_id"
            pagination={{
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 个Token`,
            }}
            loading={tokenLoading || isLoading}
            expandable={{
              expandedRowRender: (row) => (
                <Space>
                  <Tag color="blue">Token ID: {row.token_id}</Tag>
                  <Tag color={row.total_pnl >= 0 ? 'success' : 'error'}>
                    收益率(估算): {row.total_volume > 0 ? `${((row.total_pnl / row.total_volume) * 100).toFixed(2)}%` : '-'}
                  </Tag>
                </Space>
              ),
            }}
          />
        )}
      </Card>
    </div>
  )
}

export default Analytics

